import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import { API_BASE_URL } from '../../lib/config';
import { apiFetch } from '../../lib/apiFetch';
import { listMetricDefinitions, type MetricDefinitionDto } from '../../lib/metrics';
import { listCompetencies, type CompetencyDto } from '../../lib/competencies';
import { Sliders, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/kinerja/metric-weights')({
  component: MetricWeightsPage,
});

function MetricWeightsPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <MetricWeightsContent />
    </ProtectedRoute>
  );
}

type WeightRow = {
  id: string;
  metricDefinitionId: string;
  metricCode: string;
  metricName: string;
  competencyId: string;
  competencyCode: string;
  competencyName: string;
  weight: number;
  normalizationMin: number;
  normalizationMax: number;
  invert: boolean;
};

async function listWeights(companyId: string): Promise<WeightRow[]> {
  const res = await apiFetch(`${API_BASE_URL}/api/metric-competency-weights?companyId=${companyId}`);
  const json = await res.json() as { success: boolean; data?: { weights: Record<string, unknown>[] } };
  if (!json.success || !json.data) return [];
  return json.data.weights.map((w) => ({
    id: String(w.id),
    metricDefinitionId: String(w.metric_definition_id ?? w.metricDefinitionId ?? ''),
    metricCode: String(w.metricCode ?? w.metric_code ?? ''),
    metricName: String(w.metricName ?? w.metric_name ?? ''),
    competencyId: String(w.competency_id ?? w.competencyId ?? ''),
    competencyCode: String(w.competencyCode ?? w.competency_code ?? ''),
    competencyName: String(w.competencyName ?? w.competency_name ?? ''),
    weight: Number(w.weight),
    normalizationMin: Number(w.normalization_min ?? w.normalizationMin ?? 0),
    normalizationMax: Number(w.normalization_max ?? w.normalizationMax ?? 100),
    invert: Boolean(w.invert),
  }));
}

async function createWeight(
  companyId: string,
  input: { metricDefinitionId: string; competencyId: string; weight: number; normalizationMin: number; normalizationMax: number; invert: boolean },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/metric-competency-weights`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, ...input }),
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

async function updateWeight(
  id: string,
  companyId: string,
  input: { weight: number; normalizationMin: number; normalizationMax: number; invert: boolean },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/metric-competency-weights/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, ...input }),
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

async function deleteWeight(id: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/metric-competency-weights/${id}?companyId=${companyId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

async function deriveScores(companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/metric-competency-weights/derive-scores`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return res.json() as Promise<{ success: boolean; data?: { derived: number; message: string }; error?: string }>;
}

function MetricWeightsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [weights, setWeights] = useState<WeightRow[]>([]);
  const [metrics, setMetrics] = useState<MetricDefinitionDto[]>([]);
  const [competencies, setCompetencies] = useState<CompetencyDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editWeight, setEditWeight] = useState(1.0);
  const [editMin, setEditMin] = useState(0);
  const [editMax, setEditMax] = useState(100);
  const [editInvert, setEditInvert] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // New row form
  const [showAdd, setShowAdd] = useState(false);
  const [newMetricId, setNewMetricId] = useState('');
  const [newCompId, setNewCompId] = useState('');
  const [newWeight, setNewWeight] = useState(1.0);
  const [newMin, setNewMin] = useState(0);
  const [newMax, setNewMax] = useState(100);
  const [newInvert, setNewInvert] = useState(false);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  // Re-derive
  const [deriving, setDeriving] = useState(false);
  const [deriveMsg, setDeriveMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listWeights(companyId),
      listMetricDefinitions(companyId, true),
      listCompetencies({ companyId }),
    ]).then(([ws, mRes, cRes]) => {
      if (cancelled) return;
      setLoading(false);
      setWeights(ws);
      if (mRes.success && mRes.data) setMetrics(mRes.data.definitions);
      if (cRes.success && cRes.data) setCompetencies(cRes.data.competencies);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  function startEdit(row: WeightRow) {
    setEditingId(row.id);
    setEditWeight(row.weight);
    setEditMin(row.normalizationMin);
    setEditMax(row.normalizationMax);
    setEditInvert(row.invert);
  }

  async function handleSaveEdit(id: string) {
    setEditSaving(true);
    const res = await updateWeight(id, companyId, { weight: editWeight, normalizationMin: editMin, normalizationMax: editMax, invert: editInvert });
    setEditSaving(false);
    if (res.success) {
      toast.success('Weight mapping updated');
      setWeights((prev) => prev.map((w) => w.id === id ? { ...w, weight: editWeight, normalizationMin: editMin, normalizationMax: editMax, invert: editInvert } : w));
      setEditingId(null);
    } else {
      toast.error(res.error ?? 'Failed to update weight');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this weight mapping?')) return;
    const res = await deleteWeight(id, companyId);
    if (res.success) { toast.success('Weight mapping deleted'); setWeights((prev) => prev.filter((w) => w.id !== id)); }
    else toast.error(res.error ?? 'Failed to delete weight');
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newMetricId || !newCompId) return;
    setAdding(true);
    setAddError('');
    const res = await createWeight(companyId, {
      metricDefinitionId: newMetricId,
      competencyId: newCompId,
      weight: newWeight,
      normalizationMin: newMin,
      normalizationMax: newMax,
      invert: newInvert,
    });
    setAdding(false);
    if (res.success) {
      toast.success('Weight mapping added');
      // Reload weights
      const ws = await listWeights(companyId);
      setWeights(ws);
      setShowAdd(false);
      setNewMetricId('');
      setNewCompId('');
    } else {
      toast.error(res.error ?? 'Failed to add weight mapping');
      setAddError(res.error ?? 'Failed to add');
    }
  }

  async function handleDerive() {
    setDeriving(true);
    setDeriveMsg('');
    const res = await deriveScores(companyId);
    setDeriving(false);
    if (res.success && res.data) {
      toast.success(res.data.message);
      setDeriveMsg(res.data.message);
    } else {
      const msg = res.error ?? 'Error deriving scores';
      toast.error(msg);
      setDeriveMsg(msg);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Metric → Competency Weights</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure how metric snapshots are translated into competency scores (1–5 scale).
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleDerive} disabled={deriving}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${deriving ? 'animate-spin' : ''}`} />
            Re-derive All
          </Button>
          <Button size="sm" onClick={() => setShowAdd((v) => !v)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Add Mapping
          </Button>
        </div>
      </div>

      {deriveMsg && (
        <p className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{deriveMsg}</p>
      )}

      {/* Add form */}
      {showAdd && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">New Weight Mapping</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Metric</label>
                <select className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm bg-background" value={newMetricId} onChange={(e) => setNewMetricId(e.target.value)} required>
                  <option value="">Select metric…</option>
                  {metrics.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.code})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Competency</label>
                <select className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm bg-background" value={newCompId} onChange={(e) => setNewCompId(e.target.value)} required>
                  <option value="">Select competency…</option>
                  {competencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Weight (0.1–5.0)</label>
                <input type="number" step="0.1" min="0.1" max="5" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm bg-background" value={newWeight} onChange={(e) => setNewWeight(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Normalize Min</label>
                <input type="number" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm bg-background" value={newMin} onChange={(e) => setNewMin(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Normalize Max</label>
                <input type="number" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm bg-background" value={newMax} onChange={(e) => setNewMax(Number(e.target.value))} />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newInvert} onChange={(e) => setNewInvert(e.target.checked)} />
                  Invert (lower raw = higher score)
                </label>
              </div>
              {addError && <p className="text-xs text-destructive col-span-full">{addError}</p>}
              <div className="col-span-full flex gap-2">
                <Button type="submit" size="sm" disabled={adding}>{adding ? 'Adding…' : 'Add'}</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : weights.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
              <Sliders className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground">No weight mappings configured</p>
            <p className="text-xs text-muted-foreground mt-1">Add a mapping to enable automatic competency score derivation.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Metric</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Competency</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Weight</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Range</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Invert</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {weights.map((row) => (
                  <tr key={row.id} className="hover:bg-muted/30">
                    {editingId === row.id ? (
                      <>
                        <td className="px-4 py-2 text-foreground">{row.metricName}</td>
                        <td className="px-4 py-2 text-foreground">{row.competencyName}</td>
                        <td className="px-4 py-2">
                          <input type="number" step="0.1" min="0.1" max="5" className="w-16 border rounded px-1.5 py-1 text-xs" value={editWeight} onChange={(e) => setEditWeight(Number(e.target.value))} />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1 text-xs">
                            <input type="number" className="w-16 border rounded px-1.5 py-1" value={editMin} onChange={(e) => setEditMin(Number(e.target.value))} />
                            <span>–</span>
                            <input type="number" className="w-16 border rounded px-1.5 py-1" value={editMax} onChange={(e) => setEditMax(Number(e.target.value))} />
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input type="checkbox" checked={editInvert} onChange={(e) => setEditInvert(e.target.checked)} />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="default" onClick={() => handleSaveEdit(row.id)} disabled={editSaving}>{editSaving ? '…' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground">{row.metricName}</p>
                          <p className="text-xs text-muted-foreground">{row.metricCode}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-foreground">{row.competencyName}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary">{row.weight}</Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {row.normalizationMin} – {row.normalizationMax}
                        </td>
                        <td className="px-4 py-3">
                          {row.invert ? <Badge variant="destructive">Yes</Badge> : <span className="text-xs text-muted-foreground">No</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => startEdit(row)}>Edit</Button>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDelete(row.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
